/**
 * GEOMETRY-EDIT-INVARIANTS — raw-precision + Z-preserve, structurally locked.
 *
 * One-truth source for the #317 cross-cutting invariant: a geometry edit
 * (grip drag / Properties-panel coordinate edit) MUST source its baseline
 * coordinates from the LIVE geometry-document entity at FULL precision (and,
 * for a 2-D edit, MUST preserve the existing group-30/31 Z), NEVER from the
 * 3-decimal `formatNumber`-rounded `EntityPreview` string.
 *
 * PR #317 GRIPS re-violated this invariant six review rounds in a row — each
 * fix repaired the flagged (entity-type × coordinate) cell and left the
 * siblings reading the rounded preview, so the next round caught the same
 * "raw-vs-formatted precision" / "Z-drop" class in a new cell (see memory
 * `feedback-combinatorial-slice-matrix-gate`). Routing every drag/translate
 * path through these helpers means a future edit cannot reintroduce the
 * formatted-read: the document-first read lives in exactly one place.
 *
 * The helpers fall back to the formatted-preview number ONLY when no document
 * entity was threaded (the same lossy-but-correct behaviour the per-cell code
 * already had) — so they are a pure dedup of the existing invariant, not a
 * behaviour change.
 */

/**
 * Parse a (possibly undefined) formatted-preview property string into a finite
 * number, or `fallback` when it is missing / non-numeric. Mirrors the local
 * `num()` helper the geometry-edit paths used inline.
 */
function previewNum(value: string | undefined, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Source a baseline SCALAR coordinate (e.g. a circle/arc center X, a line
 * endpoint Y) from the LIVE document entity at full precision, falling back to
 * the formatted-preview value only when no document coordinate is available.
 *
 * `docValue` is the document-entity field (already narrowed by the caller to
 * the matching entity type, e.g. `docCircle?.center.x`). When it is a finite
 * number it WINS (full precision); otherwise the formatted preview
 * `previewValue` is parsed. This is the structural form of the per-cell
 * `docCircle ? docCircle.center.x : num(p["Center X"])` reads that the #317
 * rounds added one cell at a time.
 */
export function readRawCoord(
  docValue: number | undefined,
  previewValue: string | undefined,
  fallback = 0,
): number {
  if (docValue !== undefined && Number.isFinite(docValue)) return docValue;
  return previewNum(previewValue, fallback);
}

/** A 2-D / optional-Z point sourced at full precision from a document entity. */
export interface RawPoint {
  x: number;
  y: number;
  z?: number;
}

/**
 * Source a baseline POINT (x, y, and the existing Z if any) from the LIVE
 * document entity at full precision. `docPoint` is the document-entity point
 * field (already narrowed by the caller, e.g. `docCircle?.center`); the
 * formatted-preview X/Y strings are the fallback when no document point is
 * threaded.
 *
 * The existing Z is carried through verbatim (group 30/31) so a 2-D edit that
 * rebuilds the point keeps a non-zero elevation — the "Z-drop" defect class.
 */
export function readRawPoint(
  docPoint: { x: number; y: number; z?: number } | undefined,
  previewX: string | undefined,
  previewY: string | undefined,
): RawPoint {
  // Compose the scalar one-truths so X/Y carry the SAME finite guard the
  // standalone readers have: a non-finite document coordinate (NaN/Infinity)
  // falls back to the formatted preview instead of poisoning the patch — the
  // point reader must not be a looser path than `readRawCoord` (Codex P3).
  const x = readRawCoord(docPoint?.x, previewX);
  const y = readRawCoord(docPoint?.y, previewY);
  const z = readRawZ(docPoint);
  return z !== undefined ? { x, y, z } : { x, y };
}

/**
 * Source the existing group-30/31 Z from the LIVE document point so a 2-D edit
 * that rebuilds the point (Block insertion, array base point, …) does NOT
 * flatten a non-zero elevation. Returns `undefined` when no document point is
 * threaded OR its Z is absent/non-finite — the caller then omits Z (the same
 * "z not editable through the 2-D grip" fall-back the per-cell code had).
 *
 * This is the structural one-truth for the "Z-drop" defect class: a 2-D
 * insertion / base-point move keeps X/Y from the user and Z from the document.
 */
export function readRawZ(
  docPoint: { z?: number } | undefined,
): number | undefined {
  const z = docPoint?.z;
  return z !== undefined && Number.isFinite(z) ? z : undefined;
}

// DEFUSE S15 — `reconcilePointAgainstDocument` DELETED. Its only consumers were
// `reconcileLinePatchWithDocument` / `reconcileCircleArcPatchWithDocument`
// (geometryReducer), which un-rounded the rounded-preview patch that
// `applySimpleGeometryPropertyChanges` produced for a single-axis LINE/CIRCLE/ARC
// edit. The S15 typed writer edits the typed document field in place, so the
// untouched-axis raw-precision + Z-preserve invariant holds by construction and
// this reconciler is no longer needed. `readRawCoord`/`readRawPoint`/`readRawZ`/
// `readRawPointList` remain — the grip + extended-entity translators still use them.

/**
 * Source a baseline POINT LIST (LWPOLYLINE vertices / SPLINE control / fit
 * points) from the LIVE document entity at full precision. A grip drag moves
 * exactly ONE point; every UNTOUCHED point must keep its EXACT document
 * coordinates, so when the document list is present AND its count matches the
 * preview count (the index alignment a grip drag relies on) the document list
 * WINS. Falls back to the parsed-preview list when no document entity is
 * threaded or the counts disagree (a recompute changed the count).
 *
 * This is the existing `rawOrPreviewPoints` one-truth lifted into the shared
 * invariant module so the list-precision rule lives next to the scalar/point
 * rules above.
 */
export function readRawPointList(
  previewPts: Array<{ x: number; y: number }>,
  docPts: ReadonlyArray<{ x: number; y: number }> | undefined,
): Array<{ x: number; y: number }> {
  if (docPts && docPts.length === previewPts.length) {
    return docPts.map((p) => ({ x: p.x, y: p.y }));
  }
  return previewPts;
}
